/**
 * WebSocket 消息协议。
 * 全部消息用 zod schema 校验，保证前后端一致。
 *
 * 关键约束（v1.2）：
 * - typing 事件仅在 agent 方向流转，永不推给访客
 * - AI 回复一次性下发（msg 事件），不做流式
 * - 无 loading / 无 typing 提示给访客
 */

import { z } from 'zod';

// -- 客户端 → 服务端 ---------------------------------------------------

export const clientMsgSchema = z.object({
  t: z.literal('msg'),
  content: z.string().max(2000), // 允许空字符串，SessionDO 会检查 content 或 attachments 至少一个
  tempId: z.string().min(1),
  attachments: z
    .array(
      z.object({
        r2Key: z.string(),
        mime: z.string(),
        size: z.number().int().nonnegative(),
      }),
    )
    .optional(),
});

export const clientFaqSchema = z.object({
  t: z.literal('faq'),
  faqId: z.string(),
  tempId: z.string(),
});

export const clientTypingSchema = z.object({
  t: z.literal('typing'),
  isTyping: z.boolean(),
});

export const clientReadSchema = z.object({
  t: z.literal('read'),
  messageId: z.string(),
});

export const clientTakeoverSchema = z.object({
  t: z.literal('takeover'),
  enabled: z.boolean(),
});

export const clientPingSchema = z.object({
  t: z.literal('ping'),
});

export const clientEnvelopeSchema = z.discriminatedUnion('t', [
  clientMsgSchema,
  clientFaqSchema,
  clientTypingSchema,
  clientReadSchema,
  clientTakeoverSchema,
  clientPingSchema,
]);

export type ClientEnvelope = z.infer<typeof clientEnvelopeSchema>;

// -- 服务端 → 客户端 ---------------------------------------------------

export const serverAckSchema = z.object({
  t: z.literal('ack'),
  tempId: z.string(),
  messageId: z.string(),
  createdAt: z.number(),
});

export const serverMsgSchema = z.object({
  t: z.literal('msg'),
  message: z.object({
    id: z.string(),
    conversationId: z.string(),
    role: z.enum(['visitor', 'ai', 'agent', 'system']),
    content: z.string(),
    attachments: z
      .array(
        z.object({
          r2Key: z.string(),
          mime: z.string(),
          size: z.number(),
          width: z.number().optional(),
          height: z.number().optional(),
        }),
      )
      .nullable(),
    meta: z
      .object({
        model: z.string().optional(),
        latencyMs: z.number().optional(),
        source: z.enum(['faq', 'takeover', 'auto']).optional(),
        faqId: z.string().optional(),
      })
      .nullable(),
    createdAt: z.number(),
  }),
});

// typing 仅推给 agent（后台）
export const serverTypingSchema = z.object({
  t: z.literal('typing'),
  role: z.enum(['visitor', 'agent']),
  isTyping: z.boolean(),
});

export const serverStateSchema = z.object({
  t: z.literal('state'),
  aiEnabled: z.boolean(),
  agentOnline: z.boolean(),
});

export const serverErrorSchema = z.object({
  t: z.literal('error'),
  code: z.string(),
  message: z.string(),
});

export const serverPongSchema = z.object({
  t: z.literal('pong'),
});

// ---- Agent 全局订阅事件（AgentDO → 后台）---------------------------

/**
 * 有新消息事件（供后台列表徽章/详情实时更新用）
 * 与 serverMsg 的区别：不隶属某个 conversation 连接，是给全局订阅者用的
 */
export const serverNotifyMsgSchema = z.object({
  t: z.literal('notify.msg'),
  conversationId: z.string(),
  productId: z.string(),
  visitorId: z.string(),
  message: serverMsgSchema.shape.message,
});

export const serverNotifyStateSchema = z.object({
  t: z.literal('notify.state'),
  conversationId: z.string(),
  aiEnabled: z.boolean(),
});

export const serverNotifyStatusSchema = z.object({
  t: z.literal('notify.status'),
  conversationId: z.string(),
  status: z.enum(['open', 'closed', 'spam']),
});

export const serverEnvelopeSchema = z.discriminatedUnion('t', [
  serverAckSchema,
  serverMsgSchema,
  serverTypingSchema,
  serverStateSchema,
  serverErrorSchema,
  serverPongSchema,
  serverNotifyMsgSchema,
  serverNotifyStateSchema,
  serverNotifyStatusSchema,
]);

export type ServerEnvelope = z.infer<typeof serverEnvelopeSchema>;

// -- 便捷解析 ----------------------------------------------------------

export function parseClient(raw: string): ClientEnvelope | null {
  try {
    const json = JSON.parse(raw) as unknown;
    return clientEnvelopeSchema.parse(json);
  } catch {
    return null;
  }
}

export function parseServer(raw: string): ServerEnvelope | null {
  try {
    const json = JSON.parse(raw) as unknown;
    return serverEnvelopeSchema.parse(json);
  } catch {
    return null;
  }
}
